import { useAuth } from '@/contexts/AuthContext';
import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'pii_reminder_last_acknowledged';
const CHECK_INTERVAL_MS = 60_000;

const toLocalDateKey = (timestamp: number): string => {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shouldShowReminder = (enabled: boolean): boolean => {
  if (!enabled) return false;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return true;
  return toLocalDateKey(Number(stored)) !== toLocalDateKey(Date.now());
};

// One store for every reader (the dialog and the session tutorial), re-checked each minute so a new day shows it again.
const listeners = new Set<() => void>();

const subscribe = (onChange: () => void) => {
  listeners.add(onChange);
  const interval = setInterval(onChange, CHECK_INTERVAL_MS);
  return () => {
    listeners.delete(onChange);
    clearInterval(interval);
  };
};

const acknowledge = () => {
  localStorage.setItem(STORAGE_KEY, String(Date.now()));
  for (const onChange of listeners) onChange();
};

export const usePiiReminder = () => {
  const { user } = useAuth();
  // Auth-org settings (orgSettings.piiDailyReminder) are the sole source of truth.
  // The legacy selectedClinic.pii_daily_reminder fallback has been removed.
  const enabled = user?.orgSettings?.piiDailyReminder ?? false;

  const showReminder = useSyncExternalStore(subscribe, () => shouldShowReminder(enabled));

  return { showReminder, acknowledge };
};
